import { Grid, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useState } from 'react';
import { getVisitorGroupsWithAccessToDevice } from 'utils/Lock/Lock';
import SingleColumnDataGrid from 'v2/components/SingleColumnDataGrid/SingleColumnDataGrid';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useDeviceDetails from 'v2/hooks/useDeviceDetails/useDeviceDetails';

interface Props {
  deviceId: string;
}

const AccessOverviewTab: React.FC<Props> = ({ deviceId }) => {
  const { translation } = useTranslation();
  const theme = useTheme();
  const { showSnackbar } = useSnackbarContext();
  const handleFetchError = (errorMessage: string) => showSnackbar(errorMessage, { severity: 'error' });

  const { device, deviceLoading, visitorGroups, visitorGroupsLoading } = useDeviceDetails({
    handleFetchError,
    translation,
    deviceId
  });

  const lockGroupsThatHaveLockAssigned = device?.locations || [];
  const visitorGroupsWithAccess =
    visitorGroups !== undefined && device
      ? getVisitorGroupsWithAccessToDevice(device.id, lockGroupsThatHaveLockAssigned, visitorGroups)
      : [];

  const [selectedVisitorGroup, setSelectedVisitorGroup] = useState<string>();
  const [selectedLockGroup, setSelectedLockGroup] = useState<string>();

  const handleClickVisitorGroupRow = (visitorGroupId: string) => {
    setSelectedVisitorGroup((current) => (current === visitorGroupId ? undefined : visitorGroupId));
    setSelectedLockGroup(undefined);
  };

  const handleClickLockGroupRow = (lockGroupId: string) => {
    setSelectedLockGroup((current) => (current === lockGroupId ? undefined : lockGroupId));
    setSelectedVisitorGroup(undefined);
  };

  const isLockGroupHighlighted = (lockGroupId: string) => {
    if (selectedLockGroup === lockGroupId) return true;
    if (selectedVisitorGroup === undefined) return false;
    const selectedVisitorGroupWithAccess = visitorGroupsWithAccess.find(
      (visitorGroup) => visitorGroup.id === selectedVisitorGroup
    );
    return (
      selectedVisitorGroupWithAccess?.viaDeviceGroups.some((deviceGroup) => deviceGroup.id === lockGroupId) ?? false
    );
  };

  const isVisitorGroupHighlighted = (visitorGroupId: string) => {
    if (selectedVisitorGroup === visitorGroupId) return true;
    if (selectedLockGroup === undefined) return false;
    const visitorGroup = visitorGroupsWithAccess.find((group) => group.id === visitorGroupId);
    if (!visitorGroup) return false;
    return (
      visitorGroup.viaDeviceGroups.length === 0 ||
      visitorGroup.viaDeviceGroups.some((deviceGroup) => deviceGroup.id === selectedLockGroup)
    );
  };

  const isSmallScreen = useMediaQuery(theme.breakpoints.only('sm'));
  const isExtraSmallScreen = useMediaQuery(theme.breakpoints.only('xs'));
  const isLoading = deviceLoading || visitorGroupsLoading || device === undefined || visitorGroups === undefined;

  return (
    <Paper elevation={3} sx={{ padding: '24px', borderRadius: 1, width: '100%' }}>
      <Stack spacing={2}>
        <Typography variant="h5" color="textPrimary">
          {translation.access_overview}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {translation.select_item_to_see_device_access_path}
        </Typography>
        <Grid container direction={isExtraSmallScreen ? 'column' : 'row'} spacing={2} size={12}>
          <Grid container size={isSmallScreen || isExtraSmallScreen ? 12 : 6} direction="column">
            <SingleColumnDataGrid
              rows={lockGroupsThatHaveLockAssigned.map((lockGroup) => ({ id: lockGroup.id, name: lockGroup.name }))}
              headerName={translation.device_groups}
              loading={isLoading}
              isHighlighted={isLockGroupHighlighted}
              onRowClick={handleClickLockGroupRow}
              idPrefix="device-groups-"
              noRowsMessage={translation.no_device_group_for_device}
            />
          </Grid>
          <Grid container size={isSmallScreen || isExtraSmallScreen ? 12 : 6} direction="column">
            <SingleColumnDataGrid
              rows={visitorGroupsWithAccess.map((visitorGroup) => ({
                id: visitorGroup.id,
                name: visitorGroup.name
              }))}
              headerName={translation.user_groups}
              loading={isLoading}
              isHighlighted={isVisitorGroupHighlighted}
              onRowClick={handleClickVisitorGroupRow}
              idPrefix="user-groups-"
              noRowsMessage={translation.no_user_group_for_device_and_device_group}
            />
          </Grid>
        </Grid>
      </Stack>
    </Paper>
  );
};

export default AccessOverviewTab;
